import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Button,
  Card,
  CardBody,
  DatePicker,
  EmptyState,
  Skeleton,
  SkeletonCard,
  Select,
  Icon,
  showToast,
  type SelectOption,
} from '@repo/ui';
import type { DataTableColumn } from '@xtnd/web/shared/components';
import { dateUtil, numberUtil } from '@xtnd/web/shared/utils';
import type { MatterStatementFiltersState } from '../../../types';
import { DataTable, SummaryCardsGrid, AccountTransactionSection } from '../../../components';
import { ExportButton, type ExportFormat } from '../../../components/ExportButton';
import { useMatterStatement, useMatters, useExportMatterStatement } from '../../../queries';
import { groupTransactionsByAccount, accountGroupsToArray } from '../../../utils';

export function MatterStatementDashboardView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isUpdatingUrlRef = useRef(false);

  // Export mutation
  const { mutateAsync: exportStatement, isPending: isExporting } = useExportMatterStatement();

  const handleExport = async (format: ExportFormat) => {
    if (!matterId.trim() || !dateRange.startDate || !dateRange.endDate) {
      showToast.error('Please select Matter and Date Range before exporting');
      return;
    }

    try {
      const exportType = format === 'excel' ? 'xls' : format;
      await exportStatement({
        matterId,
        type: exportType as 'csv' | 'xls' | 'pdf',
        startDate: dateRange.startDate.toISOString().split('T')[0],
        endDate: dateRange.endDate.toISOString().split('T')[0],
      });
      showToast.success(`Export as ${format.toUpperCase()} started successfully`);
    } catch (error) {
      showToast.error(
        `Failed to export: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  // Default to current month (first day to last day of current month)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate, endDate };
  };

  const defaultDateRange = getCurrentMonthRange();

  // Initialize from URL params or defaults
  const [matterId, setMatterId] = useState(() => {
    return searchParams.get('matterId') || '';
  });

  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(
    () => {
      const startDateParam = searchParams.get('startDate');
      const endDateParam = searchParams.get('endDate');
      return {
        startDate: startDateParam ? new Date(startDateParam) : defaultDateRange.startDate,
        endDate: endDateParam ? new Date(endDateParam) : defaultDateRange.endDate,
      };
    }
  );

  const [filters, setFilters] = useState<MatterStatementFiltersState>(() => {
    const matterIdParam = searchParams.get('matterId') || '';
    const startDateParam = (searchParams.get('startDate') ||
      defaultDateRange.startDate.toISOString().split('T')[0]) as string;
    const endDateParam = (searchParams.get('endDate') ||
      defaultDateRange.endDate.toISOString().split('T')[0]) as string;

    return {
      matterId: matterIdParam,
      startDate: startDateParam,
      endDate: endDateParam,
    };
  });

  const [shouldLoad, setShouldLoad] = useState(() => {
    // Auto-load if matterId is present in URL
    return !!searchParams.get('matterId');
  });

  // Fetch matters list
  const { data: mattersData, isLoading: mattersLoading } = useMatters();

  // Convert matters to Select options
  const matterOptions: SelectOption[] = useMemo(() => {
    if (!mattersData) return [];
    return mattersData.map(matter => {
      const matterNumber = matter.displayNumber || matter.number || matter.id;
      return {
        value: matter.id,
        label: `${matterNumber}: ${matter.description || ''}`,
      };
    });
  }, [mattersData]);

  // Get selected matter option
  const selectedMatterOption: SelectOption | null = useMemo(() => {
    if (!matterId.trim()) return null;
    return (
      matterOptions.find(opt => opt.value === matterId) || {
        value: matterId,
        label: matterId,
      }
    );
  }, [matterId, matterOptions]);

  const { data, isLoading, error } = useMatterStatement(filters, {
    enabled: shouldLoad && !!filters.startDate && !!filters.endDate && !!filters.matterId.trim(),
  });

  // Function to update URL params
  const updateUrlParams = (newFilters: Partial<MatterStatementFiltersState>) => {
    isUpdatingUrlRef.current = true;
    const params = new URLSearchParams(searchParams);

    // Update matterId
    if (newFilters.matterId !== undefined) {
      if (newFilters.matterId) {
        params.set('matterId', newFilters.matterId);
      } else {
        params.delete('matterId');
      }
    }

    // Update date range
    if (newFilters.startDate !== undefined) {
      params.set('startDate', newFilters.startDate);
    }
    if (newFilters.endDate !== undefined) {
      params.set('endDate', newFilters.endDate);
    }

    setSearchParams(params);
    // Reset flag after a short delay
    setTimeout(() => {
      isUpdatingUrlRef.current = false;
    }, 100);
  };

  // Sync with URL params when they change (e.g., browser back/forward)
  useEffect(() => {
    if (isUpdatingUrlRef.current) return;

    const matterIdParam = searchParams.get('matterId') || '';
    const startDateParam = searchParams.get('startDate');
    const endDateParam = searchParams.get('endDate');

    // Update matterId
    if (matterIdParam !== matterId) {
      setMatterId(matterIdParam);
    }

    // Update date range
    if (startDateParam && endDateParam) {
      const startDate = new Date(startDateParam);
      const endDate = new Date(endDateParam);
      if (
        dateRange.startDate?.toISOString().split('T')[0] !== startDateParam ||
        dateRange.endDate?.toISOString().split('T')[0] !== endDateParam
      ) {
        setDateRange({ startDate, endDate });
      }

      // Update filters and trigger load if matterId exists
      setFilters({
        matterId: matterIdParam,
        startDate: startDateParam,
        endDate: endDateParam,
      });
      if (matterIdParam) {
        setShouldLoad(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = () => {
    if (!matterId.trim() || !dateRange.startDate || !dateRange.endDate) {
      return;
    }
    // Convert Date objects to yyyy-MM-dd format for API
    const startDateStr = dateRange.startDate?.toISOString().split('T')[0] || '';
    const endDateStr = dateRange.endDate?.toISOString().split('T')[0] || '';

    const newFilters = {
      matterId,
      startDate: startDateStr,
      endDate: endDateStr,
    };

    setFilters(newFilters);
    setShouldLoad(true);

    // Update URL params
    updateUrlParams(newFilters);
  };

  const summary = (data as any)?.summary;

  // Group transactions by account
  const accountGroups = useMemo(() => {
    const transactions = (data as any)?.data || [];
    if (!transactions || transactions.length === 0) return [];
    const grouped = groupTransactionsByAccount(transactions);
    return accountGroupsToArray(grouped);
  }, [data]);

  // Prepare summary cards - same format as Matter Ledger
  const summaryCards = useMemo(() => {
    if (!summary) return [];
    return [
      {
        title: 'Total Transactions',
        value: numberUtil.formatNumber(summary.totalTransactions),
        subtitle: 'Across all accounts',
        icon: 'HiClipboardList',
        color: 'text-xtnd-blue dark:text-xtnd-blue-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
      {
        title: 'Total In',
        value: numberUtil.formatCurrency(summary.totalIn || 0),
        subtitle: 'All accounts',
        icon: 'HiArrowUp',
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
      {
        title: 'Total Out',
        value: numberUtil.formatCurrency(summary.totalOut || 0),
        subtitle: 'All accounts',
        icon: 'HiArrowDown',
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
      {
        title: 'Accounts',
        value: numberUtil.formatNumber(accountGroups.length),
        subtitle: 'With transactions',
        icon: 'HiBriefcase',
        color: 'text-xtnd-blue dark:text-xtnd-blue-400',
        bgColor: 'bg-xtnd-light dark:bg-xtnd-dark-700',
        variant: 'default' as const,
        padding: 'sm' as const,
        valueSize: 'sm' as const,
      },
    ];
  }, [summary, accountGroups.length]);

  // Define columns for transaction table - same format as Matter Ledger
  const transactionColumns: DataTableColumn<any>[] = useMemo(
    () => [
      {
        key: 'date',
        header: 'Date',
        align: 'left',
        headerClassName: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600',
        renderCell: (transaction: any) => (
          <span className="text-xtnd-dark dark:text-xtnd-light">
            {transaction.transactionDate
              ? new Date(transaction.transactionDate).toLocaleDateString()
              : '-'}
          </span>
        ),
      },
      {
        key: 'source',
        header: 'Source/Destination',
        align: 'left',
        headerClassName: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600',
        renderCell: (transaction: any) => {
          const source = transaction.source || '-';
          if (source === '-') {
            return <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">-</span>;
          }

          return (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-xtnd-dark-50 text-xtnd-dark dark:bg-xtnd-dark-600 dark:text-xtnd-dark-100">
              {source}
            </span>
          );
        },
      },
      {
        key: 'account',
        header: 'Account',
        align: 'left',
        headerClassName: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600',
        renderCell: (transaction: any) => {
          if (!transaction.accountName)
            return <span className="text-xtnd-dark dark:text-xtnd-light">-</span>;

          const accountUrl = transaction.accountId
            ? `https://app.clio.com/nc/#/bank_accounts/${transaction.accountId}/transactions`
            : null;

          if (accountUrl) {
            return (
              <a
                href={accountUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline truncate max-w-xs inline-flex items-center gap-1"
                title={transaction.accountName}
              >
                <span className="truncate">{transaction.accountName}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span
              className="text-xtnd-dark dark:text-xtnd-light truncate max-w-xs"
              title={transaction.accountName}
            >
              {transaction.accountName}
            </span>
          );
        },
      },
      {
        key: 'client',
        header: 'Client',
        align: 'left',
        headerClassName: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600',
        renderCell: (transaction: any) => {
          if (!transaction.clientName)
            return <span className="text-xtnd-dark dark:text-xtnd-light">-</span>;

          const clientUrl = transaction.clientId
            ? `https://app.clio.com/nc/#/contacts/${transaction.clientId}`
            : null;

          if (clientUrl) {
            return (
              <a
                href={clientUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline truncate max-w-xs inline-flex items-center gap-1"
                title={transaction.clientName}
              >
                <span className="truncate">{transaction.clientName}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span
              className="text-xtnd-dark dark:text-xtnd-light truncate max-w-xs"
              title={transaction.clientName}
            >
              {transaction.clientName}
            </span>
          );
        },
      },
      {
        key: 'matter',
        header: 'Matter',
        align: 'left',
        headerClassName: 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600',
        renderCell: (transaction: any) => {
          if (!transaction.matterNumber)
            return <span className="text-xtnd-dark dark:text-xtnd-light font-medium">-</span>;

          const matterUrl = transaction.matterId
            ? `https://app.clio.com/nc/#/matters/${transaction.matterId}`
            : null;

          if (matterUrl) {
            return (
              <a
                href={matterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xtnd-blue dark:text-xtnd-blue-400 hover:underline font-medium inline-flex items-center gap-1"
              >
                <span>{transaction.matterNumber}</span>
                <Icon name="HiExternalLink" className="h-3 w-3 flex-shrink-0" />
              </a>
            );
          }

          return (
            <span className="text-xtnd-dark dark:text-xtnd-light font-medium">
              {transaction.matterNumber}
            </span>
          );
        },
      },
      {
        key: 'fundsIn',
        header: 'Funds In',
        align: 'right',
        headerClassName: 'bg-green-50 dark:bg-green-900/30',
        className: 'text-green-700 dark:text-green-200',
        renderCell: (transaction: any) => (
          <span className="text-green-600 dark:text-green-400">
            {transaction.fundsIn ? numberUtil.formatCurrency(transaction.fundsIn) : '-'}
          </span>
        ),
      },
      {
        key: 'fundsOut',
        header: 'Funds Out',
        align: 'right',
        headerClassName: 'bg-red-50 dark:bg-red-900/30',
        className: 'text-red-700 dark:text-red-200',
        renderCell: (transaction: any) => (
          <span className="text-red-600 dark:text-red-400">
            {transaction.fundsOut ? numberUtil.formatCurrency(transaction.fundsOut) : '-'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Search Card */}
      <Card variant="outlined">
        <CardBody>
          <div className="flex flex-wrap items-start gap-2 md:items-center md:flex-nowrap">
            {/* Matter Filter */}
            <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
                Matter <span className="text-red-500">*</span>:
              </span>
              <div className="flex-1">
                <Select<SelectOption>
                  value={selectedMatterOption}
                  onChange={option => {
                    if (option && 'value' in option) {
                      setMatterId((option as SelectOption).value);
                    } else {
                      setMatterId('');
                    }
                  }}
                  options={matterOptions}
                  placeholder="Search and select Matter..."
                  isLoading={mattersLoading}
                  disabled={mattersLoading}
                  searchable={true}
                  clearable={true}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="flex items-center gap-2 w-full md:basis-[45%] md:w-auto">
              <span className="text-sm text-xtnd-dark-600 dark:text-xtnd-dark-400 whitespace-nowrap">
                Date Range <span className="text-red-500">*</span>:
              </span>
              <div className="flex-1">
                <DatePicker
                  id="dateRange"
                  mode="range"
                  label=""
                  value={dateRange}
                  onChange={range => {
                    // Only update when both dates are selected
                    if (range.startDate && range.endDate) {
                      setDateRange({
                        startDate: range.startDate,
                        endDate: range.endDate,
                      });
                    } else {
                      setDateRange(range);
                    }
                  }}
                  format={dateUtil.DateFormat.US}
                  required
                />
              </div>
            </div>

            {/* Search Button */}
            <div className="w-full md:basis-[10%] md:w-auto">
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={!matterId.trim() || !dateRange.startDate || !dateRange.endDate}
                loading={isLoading}
                className="w-full"
                leftIcon={<Icon name="HiSearch" size="sm" />}
              >
                Search
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {!shouldLoad ? (
        <EmptyState
          icon="HiDocumentText"
          title="Enter Matter to view Operating Account statement"
          description="Please enter Matter and date range, then click Search to view the Operating Account statement"
          variant="outlined"
          padding="lg"
        />
      ) : isLoading ? (
        <div className="space-y-6">
          {/* Account Information Skeleton */}
          <Card variant="outlined">
            <CardBody>
              <Skeleton variant="text" width="200px" height="1.5rem" className="mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Skeleton variant="text" width="100px" height="1rem" className="mb-2" />
                  <Skeleton variant="text" width="150px" height="1.25rem" />
                </div>
                <div>
                  <Skeleton variant="text" width="100px" height="1rem" className="mb-2" />
                  <Skeleton variant="text" width="150px" height="1.25rem" />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Balance Summary Skeleton */}
          <Card variant="outlined">
            <CardBody>
              <Skeleton variant="text" width="200px" height="1.5rem" className="mb-4" />
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Skeleton variant="text" width="180px" height="1.25rem" className="mb-2" />
                  <div className="space-y-2">
                    <Skeleton variant="text" width="100%" height="1rem" />
                    <Skeleton variant="text" width="100%" height="1rem" />
                    <Skeleton variant="text" width="100%" height="1rem" />
                  </div>
                </div>
                <div>
                  <Skeleton variant="text" width="120px" height="1.25rem" className="mb-2" />
                  <div className="space-y-2">
                    <Skeleton variant="text" width="100%" height="1rem" />
                    <Skeleton variant="text" width="100%" height="1rem" />
                    <Skeleton variant="text" width="100%" height="1rem" />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Billing Summary Skeleton */}
          <div>
            <Skeleton variant="text" width="200px" height="1.5rem" className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>

          {/* Transaction Table Skeleton */}
          <Card variant="outlined" padding="none">
            <CardBody className="p-0">
              <div className="p-4">
                <Skeleton variant="text" width="150px" height="1.25rem" className="mb-4" />
                <div className="space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height="40px" />
                  ))}
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : error ? (
        <EmptyState
          icon="HiExclamationCircle"
          title="Failed to load transactions"
          description={error instanceof Error ? error.message : 'An error occurred while loading the transactions'}
          variant="outlined"
          padding="lg"
          actions={
            <Button
              variant="primary"
              onClick={() => {
                if (matterId.trim() && dateRange.startDate && dateRange.endDate) {
                  handleSearch();
                }
              }}
              leftIcon={<Icon name="HiRefresh" size="sm" />}
            >
              Retry
            </Button>
          }
        />
      ) : (
        <>
          {/* Overall Summary Cards */}
          {summary && summaryCards.length > 0 && (
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                Overall Summary
              </h2>
              <ExportButton
                onExport={handleExport}
                disabled={!matterId.trim() || !dateRange.startDate || !dateRange.endDate || isLoading}
                loading={isExporting}
              />
            </div>
          )}
          {summary && summaryCards.length > 0 && (
            <SummaryCardsGrid
              cards={summaryCards}
              columns={{ mobile: 1, tablet: 2, desktop: 4 }}
              gap="md"
            />
          )}

          {/* Account Sections */}
          {accountGroups.length === 0 ? (
            <EmptyState
              icon="HiClipboardList"
              title="No Operating Account transactions found"
              description="There are no Operating Account transactions for this statement period."
              variant="outlined"
              padding="lg"
            />
          ) : (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
                Transactions by Account
              </h2>
              {accountGroups.map((accountGroup, index) => (
                <AccountTransactionSection
                  key={accountGroup.accountId}
                  accountId={accountGroup.accountId}
                  accountName={accountGroup.accountName}
                  transactions={accountGroup.transactions}
                  summary={accountGroup.summary}
                  columns={transactionColumns}
                  isExpandedByDefault={index === 0}
                  showAccountLink={true}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
